import React, { useId } from 'react'

/**
 * Rysunek kafelka „VoltValue Projekt” przy raporcie oferty (prośba właściciela 2026-10-01:
 * w stylu kafelka „Oferta pod lupą” ze strony głównej).
 *
 * Kartka projektu z tym, co Projekt naprawdę zawiera: u góry schemat połączeń (moduły,
 * falownik, dom), po przewodzie płynie energia; na dole wykres produkcji miesiąc po miesiącu
 * (słupki rosną przy wejściu). Bez napisów i liczb: przy ok. 240 px byłyby nieczytelne,
 * a zmyślone kwoty czytałyby się jak prawdziwy wynik.
 *
 * Kolory na sztywno: kartka zostaje biała także w ciemnym motywie. Animacje SVG (SMIL),
 * bez nowych reguł w CSS. Rysunek jest dekoracyjny (`aria-hidden`).
 */

const NIEB = '#2563eb'
const NIEB_JASNY = '#93c5fd'
const NIEB_TLO = '#dbeafe'
const GRANAT = '#1e3a8a'
const SZARY = '#94a3b8'
const PASEK = '#e2e8f0'
const SLONCE = '#f59e0b'
const ZIELONY = '#10b981'

// Produkcja w Polsce miesiąc po miesiącu (udział szczytu), I–XII.
const MIESIACE = [0.18, 0.3, 0.55, 0.75, 0.92, 1, 0.97, 0.85, 0.62, 0.4, 0.2, 0.14]

const Modul = ({ x, y }) => (
  <g>
    <rect x={x} y={y} width="20" height="28" rx="2" fill={NIEB_TLO} stroke={NIEB} strokeWidth="1.2" />
    {[1, 2, 3].map((i) => <line key={`w${i}`} x1={x} y1={y + i * 7} x2={x + 20} y2={y + i * 7} stroke={NIEB} strokeWidth="0.5" opacity="0.5" />)}
    <line x1={x + 10} y1={y} x2={x + 10} y2={y + 28} stroke={NIEB} strokeWidth="0.5" opacity="0.5" />
  </g>
)

const GrafikaProjekt = ({ className = '' }) => {
  const id = useId().replace(/:/g, '')
  const slupek = `slupek-${id}`
  return (
    <svg viewBox="0 0 240 176" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={slupek} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={NIEB} />
          <stop offset="100%" stopColor={NIEB_JASNY} />
        </linearGradient>
      </defs>

      {/* Kartka projektu */}
      <rect x="6" y="4" width="228" height="168" rx="12" fill="#ffffff" stroke={PASEK} strokeWidth="1.2" />
      <rect x="18" y="16" width="56" height="5" rx="2.5" fill={GRANAT} opacity="0.85" />
      <rect x="18" y="25" width="36" height="3.5" rx="1.75" fill={PASEK} />
      <circle cx="214" cy="21" r="7" fill={SLONCE} opacity="0.9" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((k) => (
        <line key={k} x1={214 + 9.5 * Math.cos((k * Math.PI) / 180)} y1={21 + 9.5 * Math.sin((k * Math.PI) / 180)}
              x2={214 + 12 * Math.cos((k * Math.PI) / 180)} y2={21 + 12 * Math.sin((k * Math.PI) / 180)}
              stroke={SLONCE} strokeWidth="1.4" strokeLinecap="round" />
      ))}

      {/* ── Schemat elektryczny: moduły → falownik → dom ── */}
      <Modul x={18} y={40} />
      <Modul x={42} y={40} />
      {/* Bieguny łańcucha: rysunek ma się czytać jako schemat elektryczny, a nie ikonki. */}
      <text x="16" y="37" fontSize="7" fontWeight="700" fill={GRANAT}>+</text>
      <text x="59" y="37" fontSize="8" fontWeight="700" fill={GRANAT}>−</text>
      <path d="M66 54 H102" fill="none" stroke={NIEB} strokeWidth="2" strokeLinecap="round" />
      <path d="M142 54 H174" fill="none" stroke={SZARY} strokeWidth="2" strokeLinecap="round" />
      <circle cx="102" cy="54" r="2.2" fill={NIEB} />
      <circle cx="142" cy="54" r="2.2" fill={SZARY} />
      {/* Plakietki „V” i „A”: w Projekcie napięcia i prądy łańcuchów są policzone. */}
      <g>
        <rect x="71" y="45" width="12" height="11" rx="5.5" fill="#ffffff" stroke={NIEB} strokeWidth="1" />
        <text x="77" y="53" textAnchor="middle" fontSize="7" fontWeight="800" fill={NIEB}>V</text>
        <rect x="85" y="45" width="12" height="11" rx="5.5" fill="#ffffff" stroke={NIEB} strokeWidth="1" />
        <text x="91" y="53" textAnchor="middle" fontSize="7" fontWeight="800" fill={NIEB}>A</text>
      </g>
      {/* Falownik: obudowa z cieniem i znak przekształtnika (DC na AC), jak na schemacie w Projekcie */}
      <rect x="102" y="36" width="40" height="36" rx="7" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.3" />
      <rect x="110" y="42" width="24" height="24" rx="3" fill="#f8fafc" stroke={GRANAT} strokeWidth="1.2" />
      <line x1="110" y1="66" x2="134" y2="42" stroke={GRANAT} strokeWidth="1" />
      <line x1="113.5" y1="48" x2="120.5" y2="48" stroke={GRANAT} strokeWidth="1.1" strokeLinecap="round" />
      <line x1="113.5" y1="51" x2="120.5" y2="51" stroke={GRANAT} strokeWidth="1.1" strokeDasharray="2 1.5" strokeLinecap="round" />
      <path d="M123.5 61 q2.2 -3.6 4.4 0 t4.4 0" fill="none" stroke={GRANAT} strokeWidth="1.1" strokeLinecap="round" />
      <circle cx="137.5" cy="40.5" r="1.4" fill={ZIELONY} />
      {/* Dom: dach z okapem, ściana, drzwi i okno */}
      <path d="M173 57 L194 41 L215 57" fill="none" stroke={SZARY} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M178 54 V71 H210 V54" fill="#ffffff" stroke={SZARY} strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="189" y="61" width="8" height="10" rx="1.2" fill={NIEB_TLO} stroke={SZARY} strokeWidth="1" />
      <rect x="200" y="59" width="6" height="5" rx="1" fill={NIEB_TLO} stroke={SZARY} strokeWidth="0.9" />
      {/* Energia płynąca przewodem, spokojnie */}
      {[0, 1.6].map((opoznienie) => (
        <circle key={opoznienie} r="2.4" fill={SLONCE}>
          <animateMotion dur="3.2s" repeatCount="indefinite" begin={`${opoznienie}s`} path="M66 54 H174" />
          <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.85;1" dur="3.2s" repeatCount="indefinite" begin={`${opoznienie}s`} />
        </circle>
      ))}

      {/* ── Wykres produkcji miesiąc po miesiącu ── */}
      <line x1="20" y1="158" x2="220" y2="158" stroke={PASEK} strokeWidth="1.2" />
      {MIESIACE.map((u, i) => {
        const h = Math.round(u * 62)
        const x = 22 + i * 16.4
        return (
          <rect key={i} x={x} y={158 - h} width="11" height={h} rx="2.5" fill={`url(#${slupek})`}>
            <animate attributeName="height" from="0" to={h} dur="1.2s" begin={`${0.3 + i * 0.09}s`} fill="freeze" />
            <animate attributeName="y" from="158" to={158 - h} dur="1.2s" begin={`${0.3 + i * 0.09}s`} fill="freeze" />
          </rect>
        )
      })}
      {/* Zwrot z inwestycji: zielona fajka nad wykresem */}
      <g>
        <circle cx="206" cy="92" r="8" fill={ZIELONY} />
        <path d="M202.4 92.2 l2.4 2.6 l4.8 -5.2" fill="none" stroke="#ffffff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  )
}

export default GrafikaProjekt
